<script setup>
/* eslint-disable no-use-before-define */
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter } from 'dashboard/composables/store';
import { useStore } from 'dashboard/composables/store';
import TalkviaCreditDashboardAPI from 'dashboard/api/talkviaCreditDashboard';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ReportSectionCard from '../settings/reports/components/ReportSectionCard.vue';
import InboxSetupGuide from './InboxSetupGuide.vue';
import NoCorbanDashboard from './NoCorbanDashboard.vue';
import AnnouncementCarousel from './AnnouncementCarousel.vue';
import InstallTalkviaCard from './InstallTalkviaCard.vue';
import ConquestsCard from './ConquestsCard.vue';
import HomeHero from './HomeHero.vue';
import HomeMetricCard from './HomeMetricCard.vue';
import HomeShortcutCard from './HomeShortcutCard.vue';
import {
  HOME_PRIMARY_LINK_CLASS,
  HOME_SECONDARY_LINK_CLASS,
  homeToneClass,
} from './homeTones';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

const { t } = useI18n();
const { accountScopedRoute } = useAccount();
const store = useStore();
const currentAccount = useMapGetter('getCurrentAccount');
const currentUser = useMapGetter('getCurrentUser');
const currentRole = useMapGetter('getCurrentRole');
const inboxes = useMapGetter('inboxes/getInboxes');
const inboxUiFlags = useMapGetter('inboxes/getUIFlags');

const hasInboxes = computed(() => (inboxes.value || []).length > 0);
const isLoadingInboxes = computed(
  () => inboxUiFlags.value?.isFetching ?? false
);
const corbanFeatureFlags = [
  FEATURE_FLAGS.TALKVIA_CORBAN_FACTA_OFFLINE,
  FEATURE_FLAGS.TALKVIA_CORBAN_CLT_FGTS_QUERY,
  FEATURE_FLAGS.TALKVIA_CORBAN_CREDIT_ENGINE,
  FEATURE_FLAGS.TALKVIA_CORBAN_MANUAL_SIMULATORS,
  FEATURE_FLAGS.TALKVIA_CORBAN_PROPOSAL_PIPELINE,
];

const hasCorbanModule = computed(() => {
  const features = currentAccount.value?.features || {};
  return corbanFeatureFlags.some(feature => Boolean(features[feature]));
});

const shouldShowNoCorbanDashboard = computed(() => !hasCorbanModule.value);
// A Home só aguarda as caixas quando ainda não existe nenhuma no store.
const isLoadingHomeState = computed(
  () => !hasInboxes.value && (isPreparingHome.value || isLoadingInboxes.value)
);

const credit = ref(null);
const isLoading = ref(false);
const isPreparingHome = ref(true);

const totals = computed(() => credit.value?.totals || {});
const funnel = computed(() => credit.value?.funnel || []);
const bankIntegrations = computed(() => credit.value?.bank_integrations || []);
const recentActivity = computed(() => credit.value?.recent_activity || []);

const operatorName = computed(
  () => currentUser.value?.name?.split(' ')[0] || ''
);
const displayName = computed(
  () => operatorName.value || t('OPERATIONAL_HOME.FALLBACK_NAME')
);

const accountName = computed(() => currentAccount.value?.name || 'Talkvia');

const profileMode = computed(() => {
  if (currentUser.value?.type === 'SuperAdmin') return 'SUPER_ADMIN';
  if (currentRole.value === 'administrator') return 'ADMIN';
  return 'OPERATOR';
});

const profileCopy = computed(() => {
  const prefix = `OPERATIONAL_HOME.HERO.${profileMode.value}`;
  return {
    badge: t(`${prefix}.BADGE`),
    title: t(`${prefix}.TITLE`, { name: displayName.value }),
    description: t(`${prefix}.DESCRIPTION`),
  };
});

const primaryMetrics = computed(() => [
  {
    key: 'paid',
    label: t('OPERATIONAL_HOME.METRICS.PAID'),
    value: formatCurrency(totals.value.paid_amount),
    hint: t('OPERATIONAL_HOME.METRICS.PAID_HINT', {
      count: formatNumber(totals.value.paid_count),
    }),
    icon: 'i-hugeicons-checkmark-circle-02',
    tone: 'grass',
  },
  {
    key: 'offered',
    label: t('OPERATIONAL_HOME.METRICS.OFFERED'),
    value: formatCurrency(totals.value.offered_amount),
    hint: t('OPERATIONAL_HOME.METRICS.OFFERED_HINT', {
      count: formatNumber(totals.value.simulations_count),
    }),
    icon: 'i-hugeicons-dollar-circle',
    tone: 'teal',
  },
  {
    key: 'typed',
    label: t('OPERATIONAL_HOME.METRICS.TYPED'),
    value: formatCurrency(totals.value.typed_amount),
    hint: t('OPERATIONAL_HOME.METRICS.TYPED_HINT', {
      count: formatNumber(totals.value.proposals_count),
    }),
    icon: 'i-hugeicons-file-edit',
    tone: 'blue',
  },
  {
    key: 'conversion',
    label: t('OPERATIONAL_HOME.METRICS.CONVERSION'),
    value: formatPercent(totals.value.lead_to_paid_conversion),
    hint: t('OPERATIONAL_HOME.METRICS.CONVERSION_HINT'),
    icon: 'i-hugeicons-route-01',
    tone: 'amber',
  },
]);

// "Manual e Tour" permanece ao lado de Contatos.
const quickActions = computed(() => [
  {
    key: 'conversations',
    title: t('OPERATIONAL_HOME.SHORTCUTS.CONVERSATIONS.TITLE'),
    description: t('OPERATIONAL_HOME.SHORTCUTS.CONVERSATIONS.DESCRIPTION'),
    icon: 'i-hugeicons-bubble-chat',
    to: accountScopedRoute('talkvia_conversations'),
    tone: 'blue',
  },
  {
    key: 'contacts',
    title: t('OPERATIONAL_HOME.SHORTCUTS.CONTACTS.TITLE'),
    description: t('OPERATIONAL_HOME.SHORTCUTS.CONTACTS.DESCRIPTION'),
    icon: 'i-hugeicons-user-group',
    to: accountScopedRoute('contacts_dashboard_index', {}, { page: 1 }),
    tone: 'teal',
  },
  {
    key: 'tour',
    title: t('OPERATIONAL_HOME.SHORTCUTS.TOUR.TITLE'),
    description: t('OPERATIONAL_HOME.SHORTCUTS.TOUR.DESCRIPTION'),
    icon: 'i-hugeicons-book-open-01',
    to: accountScopedRoute('talkvia_tour'),
    tone: 'violet',
  },
  ...(profileMode.value === 'OPERATOR'
    ? []
    : [
        {
          key: 'creditPulse',
          title: t('OPERATIONAL_HOME.SHORTCUTS.CREDIT_PULSE.TITLE'),
          description: t('OPERATIONAL_HOME.SHORTCUTS.CREDIT_PULSE.DESCRIPTION'),
          icon: 'i-hugeicons-activity-01',
          to: accountScopedRoute('talkvia_credit_dashboard'),
          tone: 'grass',
        },
      ]),
  {
    key: 'proposals',
    title: t('OPERATIONAL_HOME.SHORTCUTS.PROPOSALS.TITLE'),
    description: t('OPERATIONAL_HOME.SHORTCUTS.PROPOSALS.DESCRIPTION'),
    icon: 'i-hugeicons-task-done-01',
    to: accountScopedRoute('talkvia_proposals'),
    tone: 'slate',
  },
  {
    key: 'calendar',
    title: t('OPERATIONAL_HOME.SHORTCUTS.CALENDAR.TITLE'),
    description: t('OPERATIONAL_HOME.SHORTCUTS.CALENDAR.DESCRIPTION'),
    icon: 'i-hugeicons-calendar-03',
    to: accountScopedRoute('talkvia_calendar'),
    tone: 'amber',
  },
]);

const operationalCards = computed(() => [
  {
    key: 'banks',
    label: t('OPERATIONAL_HOME.PULSE.BANKS'),
    value: formatNumber(bankIntegrations.value.length),
    detail:
      bankIntegrations.value.length > 0
        ? bankIntegrations.value
            .slice(0, 3)
            .map(bank => bank.label)
            .join(', ')
        : t('OPERATIONAL_HOME.PULSE.BANKS_EMPTY'),
    icon: 'i-hugeicons-bank',
  },
  {
    key: 'losses',
    label: t('OPERATIONAL_HOME.PULSE.LOSSES'),
    value: formatCurrency(totals.value.lost_amount),
    detail: t('OPERATIONAL_HOME.PULSE.LOSSES_DETAIL', {
      count: formatNumber(totals.value.lost_count),
    }),
    icon: 'i-hugeicons-chart-down',
  },
  {
    key: 'production',
    label: t('OPERATIONAL_HOME.PULSE.PRODUCTION'),
    value: formatCurrency(totals.value.production_per_lead),
    detail: t('OPERATIONAL_HOME.PULSE.PRODUCTION_DETAIL', {
      count: formatNumber(totals.value.leads_count),
    }),
    icon: 'i-hugeicons-user-check-01',
  },
]);

const strongestFunnelStage = computed(() => {
  if (!funnel.value.length) return null;
  return funnel.value.reduce((best, stage) =>
    Number(stage.conversion || 0) > Number(best.conversion || 0) ? stage : best
  );
});

const nextBestAction = computed(() => {
  const stage = funnel.value.find(item => item.status === 'below');
  if (!stage) {
    return {
      title: t('OPERATIONAL_HOME.NEXT_ACTION.HEALTHY_TITLE'),
      body: t('OPERATIONAL_HOME.NEXT_ACTION.HEALTHY_BODY'),
      to: accountScopedRoute('talkvia_credit_dashboard'),
    };
  }
  return {
    title: t('OPERATIONAL_HOME.NEXT_ACTION.ATTENTION_TITLE', {
      stage: stage.label.toLowerCase(),
    }),
    body: t('OPERATIONAL_HOME.NEXT_ACTION.ATTENTION_BODY', {
      delta: Math.abs(stage.delta),
    }),
    to: accountScopedRoute('talkvia_proposals'),
  };
});

const funnelHealth = computed(() => {
  const below = funnel.value.filter(stage => stage.status === 'below').length;
  if (!funnel.value.length) {
    return {
      label: t('OPERATIONAL_HOME.FUNNEL.WAITING'),
      detail: t('OPERATIONAL_HOME.FUNNEL.WAITING_DETAIL'),
      tone: 'slate',
      icon: 'i-hugeicons-loading-03',
    };
  }
  if (below === 0) {
    return {
      label: t('OPERATIONAL_HOME.FUNNEL.HEALTHY'),
      detail: t('OPERATIONAL_HOME.FUNNEL.HEALTHY_DETAIL'),
      tone: 'grass',
      icon: 'i-hugeicons-security-check',
    };
  }
  return {
    label: t('OPERATIONAL_HOME.FUNNEL.BELOW', { count: below }, below),
    detail: t('OPERATIONAL_HOME.FUNNEL.BELOW_DETAIL'),
    tone: 'amber',
    icon: 'i-hugeicons-alert-02',
  };
});

const loadHome = async () => {
  isLoading.value = true;
  try {
    const { data } = await TalkviaCreditDashboardAPI.get({ period: '30d' });
    credit.value = data;
  } catch {
    useAlert(t('OPERATIONAL_HOME.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

function formatCurrency(value) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
}

function formatNumber(value) {
  return new Intl.NumberFormat('pt-BR').format(Number(value || 0));
}

function formatPercent(value) {
  return `${Number(value || 0).toLocaleString('pt-BR', {
    maximumFractionDigits: 1,
  })}%`;
}

function formatTime(value) {
  if (!value) return '';
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}

function funnelWidth(stage) {
  return `${Math.min(Number(stage.top_percentage || 0), 100)}%`;
}

watch(
  hasCorbanModule,
  hasModule => {
    if (!hasModule || credit.value || isLoading.value) return;

    loadHome();
  },
  { immediate: true }
);

onMounted(async () => {
  try {
    await store.dispatch('inboxes/get');
  } finally {
    isPreparingHome.value = false;
  }
});
</script>

<template>
  <div
    v-if="isLoadingHomeState"
    class="flex h-full items-center justify-center"
    :aria-label="t('OPERATIONAL_HOME.LOADING')"
  >
    <Spinner :size="32" />
  </div>
  <InboxSetupGuide v-else-if="!hasInboxes" />
  <NoCorbanDashboard
    v-else-if="shouldShowNoCorbanDashboard"
    :account-name="accountName"
    :operator-name="operatorName"
    :has-corban-module="hasCorbanModule"
  />
  <main v-else class="h-full overflow-y-auto bg-n-background">
    <div class="mx-auto w-full max-w-[90rem] px-4 pb-12 sm:px-6 lg:px-8">
      <HomeHero
        :eyebrow="`${profileCopy.badge} · ${accountName}`"
        :title="profileCopy.title"
        :description="profileCopy.description"
      >
        <router-link
          :to="
            accountScopedRoute(
              'contacts_dashboard_index',
              {},
              { page: 1, new_contact: 'true' }
            )
          "
          :class="HOME_PRIMARY_LINK_CLASS"
        >
          <span class="i-hugeicons-user-add-01 size-4" />
          {{ t('OPERATIONAL_HOME.ACTIONS.NEW_CUSTOMER') }}
        </router-link>
        <router-link
          :to="accountScopedRoute('talkvia_credit_dashboard')"
          :class="HOME_SECONDARY_LINK_CLASS"
        >
          <span class="i-hugeicons-chart-line-data-01 size-4" />
          {{ t('OPERATIONAL_HOME.ACTIONS.CREDIT_DASHBOARD') }}
        </router-link>
      </HomeHero>

      <div class="grid grid-cols-1 gap-5">
        <div class="flex flex-col gap-5 xl:flex-row">
          <AnnouncementCarousel class="xl:flex-[2]" />
          <ConquestsCard
            class="xl:min-w-[20rem] xl:flex-1"
            :user-id="currentUser?.id"
          />
        </div>

        <InstallTalkviaCard />

        <section :aria-label="t('OPERATIONAL_HOME.METRICS.TITLE')">
          <div class="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h2 class="mb-0 text-base font-semibold text-n-slate-12">
              {{ t('OPERATIONAL_HOME.METRICS.TITLE') }}
            </h2>
            <span
              class="inline-flex items-center gap-1.5 rounded-md bg-n-solid-2 px-2 py-1 text-xs font-medium text-n-slate-11"
            >
              <span class="i-hugeicons-calendar-03 size-3.5" />
              {{ t('OPERATIONAL_HOME.METRICS.PERIOD') }}
            </span>
          </div>
          <div class="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <HomeMetricCard
              v-for="metric in primaryMetrics"
              :key="metric.key"
              :label="metric.label"
              :value="metric.value"
              :hint="metric.hint"
              :icon="metric.icon"
              :tone="metric.tone"
              :loading="isLoading"
            />
          </div>
        </section>

        <div class="grid gap-5 xl:grid-cols-3">
          <section
            class="xl:col-span-2"
            :aria-label="t('OPERATIONAL_HOME.SHORTCUTS.TITLE')"
          >
            <div class="mb-3">
              <h2 class="mb-0 text-base font-semibold text-n-slate-12">
                {{ t('OPERATIONAL_HOME.SHORTCUTS.TITLE') }}
              </h2>
              <p class="mb-0 text-xs text-n-slate-11">
                {{ t('OPERATIONAL_HOME.SHORTCUTS.DESCRIPTION') }}
              </p>
            </div>
            <div class="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <HomeShortcutCard
                v-for="action in quickActions"
                :key="action.key"
                :title="action.title"
                :description="action.description"
                :icon="action.icon"
                :tone="action.tone"
                :to="action.to"
              />
            </div>
          </section>

          <ReportSectionCard
            :title="t('OPERATIONAL_HOME.NEXT_ACTION.TITLE')"
            :description="t('OPERATIONAL_HOME.NEXT_ACTION.SUBTITLE_OPERATION')"
            icon="i-hugeicons-idea-01"
          >
            <div v-if="isLoading" class="grid gap-2" aria-busy="true">
              <span
                class="h-5 w-2/3 rounded bg-n-alpha-2 motion-safe:animate-pulse"
              />
              <span
                class="h-4 w-full rounded bg-n-alpha-2 motion-safe:animate-pulse"
              />
              <span
                class="h-4 w-4/5 rounded bg-n-alpha-2 motion-safe:animate-pulse"
              />
            </div>
            <template v-else>
              <h3 class="mb-1.5 text-base font-semibold text-n-slate-12">
                {{ nextBestAction.title }}
              </h3>
              <p class="mb-4 text-sm leading-6 text-n-slate-11">
                {{ nextBestAction.body }}
              </p>
              <router-link
                :to="nextBestAction.to"
                :class="HOME_SECONDARY_LINK_CLASS"
              >
                {{ t('OPERATIONAL_HOME.ACTIONS.OPEN_CONTEXT') }}
                <span class="i-hugeicons-arrow-right-01 size-4" />
              </router-link>
            </template>
          </ReportSectionCard>
        </div>

        <div class="grid gap-5 xl:grid-cols-3">
          <ReportSectionCard
            :title="t('OPERATIONAL_HOME.FUNNEL.TITLE')"
            :description="t('OPERATIONAL_HOME.FUNNEL.DESCRIPTION')"
            icon="i-hugeicons-route-01"
          >
            <div v-if="isLoading" class="grid gap-3" aria-busy="true">
              <span
                v-for="index in 4"
                :key="index"
                class="h-8 rounded bg-n-alpha-2 motion-safe:animate-pulse"
              />
            </div>
            <template v-else>
              <div
                class="mb-4 flex items-start gap-3 rounded-xl border border-n-weak p-3 dark:border-n-strong"
              >
                <span
                  class="grid size-8 shrink-0 place-items-center rounded-lg"
                  :class="homeToneClass(funnelHealth.tone)"
                  aria-hidden="true"
                >
                  <span class="size-4" :class="funnelHealth.icon" />
                </span>
                <div class="min-w-0">
                  <p class="mb-0 text-sm font-semibold text-n-slate-12">
                    {{ funnelHealth.label }}
                  </p>
                  <p class="mb-0 mt-0.5 text-xs leading-5 text-n-slate-11">
                    {{ funnelHealth.detail }}
                  </p>
                </div>
              </div>
              <ul class="m-0 grid list-none gap-3.5 p-0">
                <li v-for="stage in funnel" :key="stage.key">
                  <div class="mb-1.5 flex items-center justify-between gap-3">
                    <span class="truncate text-sm font-medium text-n-slate-12">
                      {{ stage.label }}
                    </span>
                    <span class="text-xs tabular-nums text-n-slate-11">
                      {{ formatPercent(stage.conversion) }}
                    </span>
                  </div>
                  <div class="h-2 rounded-full bg-n-slate-4">
                    <div
                      class="h-2 rounded-full"
                      :class="
                        stage.status === 'below'
                          ? 'bg-n-amber-9'
                          : 'bg-n-teal-9'
                      "
                      :style="{ width: funnelWidth(stage) }"
                    />
                  </div>
                </li>
              </ul>
              <p
                v-if="!funnel.length"
                class="mb-0 rounded-xl border border-dashed border-n-weak p-4 text-sm text-n-slate-11 dark:border-n-strong"
              >
                {{ t('OPERATIONAL_HOME.FUNNEL.EMPTY') }}
              </p>
            </template>
          </ReportSectionCard>

          <ReportSectionCard
            class="xl:col-span-2"
            :title="t('OPERATIONAL_HOME.PULSE.TITLE')"
            :description="t('OPERATIONAL_HOME.PULSE.DESCRIPTION')"
            icon="i-hugeicons-activity-01"
          >
            <template v-if="strongestFunnelStage && !isLoading" #actions>
              <span
                class="rounded-md bg-n-grass-3 px-2 py-1 text-xs font-medium text-n-grass-11"
              >
                {{
                  t('OPERATIONAL_HOME.PULSE.BEST_STAGE', {
                    stage: strongestFunnelStage.label,
                  })
                }}
              </span>
            </template>

            <div class="mb-5 grid gap-3 md:grid-cols-3">
              <div
                v-for="card in operationalCards"
                :key="card.key"
                class="flex min-w-0 items-start gap-3 rounded-xl bg-n-solid-2 p-3"
              >
                <span
                  class="grid size-8 shrink-0 place-items-center rounded-lg bg-n-solid-1 text-n-slate-11"
                  aria-hidden="true"
                >
                  <span class="size-4" :class="card.icon" />
                </span>
                <div class="min-w-0">
                  <span class="block truncate text-xs text-n-slate-11">
                    {{ card.label }}
                  </span>
                  <span
                    v-if="isLoading"
                    class="mt-1 block h-5 w-20 rounded bg-n-alpha-2 motion-safe:animate-pulse"
                  />
                  <template v-else>
                    <strong
                      class="block text-lg font-semibold tabular-nums text-n-slate-12"
                    >
                      {{ card.value }}
                    </strong>
                    <p
                      class="mb-0 truncate text-xs text-n-slate-11"
                      :title="card.detail"
                    >
                      {{ card.detail }}
                    </p>
                  </template>
                </div>
              </div>
            </div>

            <h3 class="mb-2 text-sm font-semibold text-n-slate-12">
              {{ t('OPERATIONAL_HOME.PULSE.ACTIVITY_TITLE') }}
            </h3>
            <div v-if="isLoading" class="grid gap-2" aria-busy="true">
              <span
                v-for="index in 3"
                :key="index"
                class="h-12 rounded-lg bg-n-alpha-2 motion-safe:animate-pulse"
              />
            </div>
            <ul
              v-else-if="recentActivity.length"
              class="m-0 list-none divide-y divide-n-weak p-0"
            >
              <li
                v-for="item in recentActivity.slice(0, 5)"
                :key="`${item.type}-${item.occurred_at}-${item.title}`"
                class="flex items-center gap-3 py-2.5"
              >
                <span
                  class="grid size-8 shrink-0 place-items-center rounded-lg bg-n-solid-2 text-n-slate-11"
                  aria-hidden="true"
                >
                  <span class="i-hugeicons-activity-01 size-4" />
                </span>
                <div class="min-w-0 flex-1">
                  <p class="mb-0 truncate text-sm font-medium text-n-slate-12">
                    {{ item.title }}
                  </p>
                  <p class="mb-0 truncate text-xs text-n-slate-11">
                    {{ item.subtitle }} · {{ formatTime(item.occurred_at) }}
                  </p>
                </div>
                <span
                  class="shrink-0 text-sm font-semibold tabular-nums text-n-slate-12"
                >
                  {{ formatCurrency(item.amount) }}
                </span>
              </li>
            </ul>
            <p
              v-else
              class="mb-0 rounded-xl border border-dashed border-n-weak p-4 text-sm text-n-slate-11 dark:border-n-strong"
            >
              {{ t('OPERATIONAL_HOME.PULSE.ACTIVITY_EMPTY') }}
            </p>
          </ReportSectionCard>
        </div>
      </div>
    </div>
  </main>
</template>
